import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Clock, MapPin, Camera, CheckCircle, XCircle, AlertTriangle,
  Calendar, User, Users, ChevronLeft, ChevronRight, Loader2, X
} from "lucide-react";
import { toast } from "sonner";

export default function AttendancePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("punch");
  const [loading, setLoading] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [circleSummary, setCircleSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [gpsCapturing, setGpsCapturing] = useState(false);
  const [currentGps, setCurrentGps] = useState(null);
  
  // Selfie capture states
  const [showCamera, setShowCamera] = useState(false);
  const [selfieData, setSelfieData] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const isManager = ["supervisor", "ci", "admin", "package_head"].includes(user?.role);

  // Fetch today's attendance
  const fetchToday = useCallback(async () => {
    try {
      const res = await api.get("/attendance/my-today");
      setTodayData(res.data);
    } catch (err) {
      console.error("Error fetching today:", err);
    }
  }, []);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get(`/attendance/my-history?month=${selectedMonth}`);
      setHistoryData(res.data);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  }, [selectedMonth]);

  // Fetch circle summary (managers only)
  const fetchCircleSummary = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await api.get("/attendance/circle-summary");
      setCircleSummary(res.data.summary);
    } catch (err) {
      console.error("Error fetching circle summary:", err);
    }
  }, [isManager]);

  // Fetch alerts (managers only)
  const fetchAlerts = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await api.get("/attendance/alerts?resolved=false");
      setAlerts(res.data.items || []);
    } catch (err) {
      console.error("Error fetching alerts:", err);
    }
  }, [isManager]);

  useEffect(() => {
    fetchToday();
    fetchHistory();
    fetchCircleSummary();
    fetchAlerts();
  }, [fetchToday, fetchHistory, fetchCircleSummary, fetchAlerts]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Start camera for selfie
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 320, height: 240 }
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      console.error("Camera error:", err);
      // Mock selfie for testing
      setSelfieData("mock_selfie_base64");
      toast.warning("Camera not available - using mock selfie");
    }
  };

  // Capture selfie from video stream
  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth || 320;
      canvas.height = video.videoHeight || 240;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get base64 data
      const imageData = canvas.toDataURL("image/jpeg", 0.7);
      setSelfieData(imageData);
      
      // Stop camera
      stopCamera();
      toast.success("Selfie captured!");
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // Clear selfie
  const clearSelfie = () => {
    setSelfieData(null);
  };

  // Capture GPS
  const captureGps = () => {
    setGpsCapturing(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentGps({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setGpsCapturing(false);
        },
        (error) => {
          console.error("GPS Error:", error);
          // Mock GPS for testing
          setCurrentGps({ lat: 25.3176, lng: 82.9739, accuracy: 10 });
          setGpsCapturing(false);
          toast.warning("Using mock GPS location");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setCurrentGps({ lat: 25.3176, lng: 82.9739, accuracy: 10 });
      setGpsCapturing(false);
      toast.warning("Geolocation not supported, using mock");
    }
  };

  // Punch In
  const handlePunchIn = async () => {
    if (!currentGps) {
      toast.error("Please capture GPS first");
      return;
    }
    if (!selfieData) {
      toast.error("Please capture selfie first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/attendance/punch-in", {
        gps_lat: currentGps.lat,
        gps_lng: currentGps.lng,
        gps_accuracy_m: currentGps.accuracy,
        device_id: `web-${navigator.userAgent.slice(0, 20)}`,
        selfie_base64: selfieData
      });
      toast.success(res.data.message);
      if (res.data.warnings?.length) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      // Reset states
      setCurrentGps(null);
      setSelfieData(null);
      fetchToday();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Punch-in failed");
    } finally {
      setLoading(false);
    }
  };

  // Punch Out
  const handlePunchOut = async () => {
    if (!currentGps) {
      toast.error("Please capture GPS first");
      return;
    }
    if (!selfieData) {
      toast.error("Please capture selfie first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/attendance/punch-out", {
        gps_lat: currentGps.lat,
        gps_lng: currentGps.lng,
        gps_accuracy_m: currentGps.accuracy,
        device_id: `web-${navigator.userAgent.slice(0, 20)}`,
        selfie_base64: selfieData
      });
      toast.success(res.data.message);
      if (res.data.warnings?.length) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      // Reset states
      setCurrentGps(null);
      setSelfieData(null);
      fetchToday();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Punch-out failed");
    } finally {
      setLoading(false);
    }
  };

  // Resolve Alert
  const resolveAlert = async (alertId, action) => {
    try {
      await api.post(`/attendance/alerts/${alertId}/resolve?resolution_action=${action}`);
      toast.success("Alert resolved");
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resolve alert");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "present": return "bg-green-100 text-green-700";
      case "half_day": return "bg-yellow-100 text-yellow-700";
      case "absent": return "bg-red-100 text-red-700";
      case "leave": return "bg-blue-100 text-blue-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Attendance</h1>
          <p className="text-sm text-slate-500">Punch In/Out, Leave & History</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-slate-600">{new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
          <div className="text-xs text-slate-400">{new Date().toLocaleTimeString("en-IN")}</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab("punch")}
          className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "punch" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="tab-punch"
        >
          <Clock className="inline-block w-4 h-4 mr-1" />
          Punch In/Out
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "history" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="tab-history"
        >
          <Calendar className="inline-block w-4 h-4 mr-1" />
          My History
        </button>
        {isManager && (
          <>
            <button
              onClick={() => setActiveTab("team")}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "team" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="tab-team"
            >
              <Users className="inline-block w-4 h-4 mr-1" />
              Team/Circle
            </button>
            <button
              onClick={() => setActiveTab("alerts")}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "alerts" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="tab-alerts"
            >
              <AlertTriangle className="inline-block w-4 h-4 mr-1" />
              Alerts
              {alerts.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{alerts.length}</span>}
            </button>
          </>
        )}
      </div>

      {/* Punch In/Out Tab */}
      {activeTab === "punch" && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Punch Card */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Today's Punch
            </h3>

            {/* Status Display */}
            {todayData?.attendance && (
              <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-600">Status</span>
                  <span className={`px-2 py-1 text-xs font-bold rounded uppercase ${getStatusColor(todayData.attendance.status)}`}>
                    {todayData.attendance.status}
                  </span>
                </div>
                {todayData.attendance.punch_in_time && (
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-600">Punch In</span>
                    <span className="text-sm font-medium">{new Date(todayData.attendance.punch_in_time).toLocaleTimeString("en-IN")}</span>
                  </div>
                )}
                {todayData.attendance.punch_out_time && (
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-600">Punch Out</span>
                    <span className="text-sm font-medium">{new Date(todayData.attendance.punch_out_time).toLocaleTimeString("en-IN")}</span>
                  </div>
                )}
                {todayData.attendance.total_hours != null && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Total Hours</span>
                    <span className="text-sm font-medium">{todayData.attendance.total_hours}h</span>
                  </div>
                )}
                {todayData.attendance.is_late && (
                  <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Late Arrival
                  </div>
                )}
              </div>
            )}

            {/* Selfie Capture */}
            <div className="mb-4">
              {!selfieData ? (
                <button
                  onClick={startCamera}
                  className="w-full py-3 border-2 border-dashed border-slate-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                  data-testid="capture-selfie-btn"
                >
                  <Camera className="w-5 h-5 text-slate-400" />
                  Tap to Capture Selfie
                </button>
              ) : (
                <div className="relative">
                  <div className="flex items-center justify-center p-2 bg-green-50 border border-green-200 rounded-lg">
                    {selfieData.startsWith("data:image") ? (
                      <img src={selfieData} alt="Selfie" className="w-20 h-20 object-cover rounded" />
                    ) : (
                      <div className="w-20 h-20 bg-green-100 rounded flex items-center justify-center">
                        <Camera className="w-8 h-8 text-green-500" />
                      </div>
                    )}
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-green-700">Selfie Captured</p>
                      <p className="text-xs text-green-600">Ready for punch</p>
                    </div>
                    <button
                      onClick={clearSelfie}
                      className="p-1 text-slate-400 hover:text-red-500"
                      data-testid="clear-selfie-btn"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* GPS Capture */}
            <div className="mb-4">
              <button
                onClick={captureGps}
                disabled={gpsCapturing}
                className="w-full py-3 border-2 border-dashed border-slate-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                data-testid="capture-gps-btn"
              >
                {gpsCapturing ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Capturing GPS...</>
                ) : currentGps ? (
                  <><MapPin className="w-5 h-5 text-green-500" /> GPS: {currentGps.lat.toFixed(4)}, {currentGps.lng.toFixed(4)}</>
                ) : (
                  <><MapPin className="w-5 h-5 text-slate-400" /> Tap to Capture GPS</>
                )}
              </button>
            </div>

            {/* Punch Buttons */}
            <div className="space-y-3">
              {todayData?.can_punch_in && (
                <button
                  onClick={handlePunchIn}
                  disabled={loading || !currentGps || !selfieData}
                  className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  data-testid="punch-in-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  PUNCH IN
                </button>
              )}
              {todayData?.can_punch_out && (
                <button
                  onClick={handlePunchOut}
                  disabled={loading || !currentGps || !selfieData}
                  className="w-full py-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  data-testid="punch-out-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
                  PUNCH OUT
                </button>
              )}
              {!todayData?.can_punch_in && !todayData?.can_punch_out && (
                <div className="py-4 bg-slate-100 text-slate-500 text-center rounded-lg">
                  ✅ Attendance completed for today
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats Card */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              This Month Summary
            </h3>
            {historyData?.summary ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{historyData.summary.present_days}</div>
                  <div className="text-xs text-green-700">Present</div>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-600">{historyData.summary.half_days}</div>
                  <div className="text-xs text-yellow-700">Half Days</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{historyData.summary.absent_days}</div>
                  <div className="text-xs text-red-700">Absent</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{historyData.summary.leave_days}</div>
                  <div className="text-xs text-blue-700">Leave</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">{historyData.summary.late_count}</div>
                  <div className="text-xs text-amber-700">Late</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">{historyData.summary.overtime_hours?.toFixed(1) || 0}</div>
                  <div className="text-xs text-purple-700">OT Hours</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">Loading...</div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Attendance History</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => {
                const d = new Date(selectedMonth + "-01");
                d.setMonth(d.getMonth() - 1);
                setSelectedMonth(d.toISOString().slice(0, 7));
              }} className="p-1 hover:bg-slate-100 rounded">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-1 border border-slate-200 rounded text-sm"
              />
              <button onClick={() => {
                const d = new Date(selectedMonth + "-01");
                d.setMonth(d.getMonth() + 1);
                setSelectedMonth(d.toISOString().slice(0, 7));
              }} className="p-1 hover:bg-slate-100 rounded">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Punch In</th>
                  <th className="text-left py-2 px-3">Punch Out</th>
                  <th className="text-left py-2 px-3">Hours</th>
                  <th className="text-left py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {historyData?.records?.length ? historyData.records.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{r.attendance_date}</td>
                    <td className="py-2 px-3">{r.punch_in_time ? new Date(r.punch_in_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="py-2 px-3">{r.punch_out_time ? new Date(r.punch_out_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="py-2 px-3">{r.total_hours?.toFixed(1) || "—"}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${getStatusColor(r.status)}`}>{r.status}</span>
                      {r.is_late && <span className="ml-1 text-xs text-amber-500">⏰</span>}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">No records for this month</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Team/Circle Tab (Managers) */}
      {activeTab === "team" && isManager && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">Today's Circle Summary</h3>
          {circleSummary ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <div className="text-3xl font-bold text-slate-700">{circleSummary.total_employees}</div>
                <div className="text-xs text-slate-500">Total Employees</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-3xl font-bold text-green-600">{circleSummary.present}</div>
                <div className="text-xs text-green-700">Present</div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <div className="text-3xl font-bold text-red-600">{circleSummary.absent}</div>
                <div className="text-xs text-red-700">Absent</div>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <div className="text-3xl font-bold text-amber-600">{circleSummary.late}</div>
                <div className="text-xs text-amber-700">Late</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          )}
        </div>
      )}

      {/* Alerts Tab (Managers) */}
      {activeTab === "alerts" && isManager && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Open Alerts ({alerts.length})
          </h3>
          {alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-slate-800">{alert.employee_name}</div>
                      <div className="text-sm text-amber-700 mt-1">{alert.alert_type?.replace(/_/g, " ")}</div>
                      <div className="text-xs text-slate-500 mt-1">{alert.alert_detail}</div>
                      <div className="text-xs text-slate-400 mt-1">{alert.alert_date}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => resolveAlert(alert.id, "regularized")}
                        className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                      >
                        Regularize
                      </button>
                      <button
                        onClick={() => resolveAlert(alert.id, "ignored")}
                        className="px-3 py-1 bg-slate-500 text-white text-xs rounded hover:bg-slate-600"
                      >
                        Ignore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">No open alerts 🎉</div>
          )}
        </div>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-testid="camera-modal">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800">Capture Selfie</h3>
              <button onClick={stopCamera} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="bg-black rounded-lg overflow-hidden mb-4 aspect-video flex items-center justify-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-center text-sm text-slate-500 mb-4">
                Position your face in the frame and tap capture
              </p>
              <button
                onClick={captureSelfie}
                className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                data-testid="take-photo-btn"
              >
                <Camera className="w-5 h-5" />
                CAPTURE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Canvas for Selfie Processing */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
